var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Type } from '@sinclair/typebox';
import { toMinorUnits } from "../../utils.mjs";
import { elementInstance } from "../instance.mjs";
import { postWithRetries } from "../../http.mjs";
import { logger } from "../../log.mjs";
import { expressCheckoutElementId, paymentIntentClientSecret } from "./elements.mjs";
import { getAddresses, loadStripeIfNeeded } from "./utils.mjs";
export const initializeStripeExpressElements = (state, cartCheckout, cartPayment) => __awaiter(void 0, void 0, void 0, function* () {
    elementInstance({
        elementId: expressCheckoutElementId,
        idempotencyKey: state.id + state.version,
        onMount: (e) => __awaiter(void 0, void 0, void 0, function* () {
            e.style.visibility = 'hidden';
            const stripe = yield loadStripeIfNeeded(cartPayment);
            const elements = stripe.elements({
                amount: toMinorUnits(cartCheckout.leftToPayInclTax),
                mode: 'payment',
                currency: state.currency.toLowerCase(),
            });
            const expressCheckoutElement = elements.create('expressCheckout');
            expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
                if (availablePaymentMethods) {
                    e.style.visibility = 'initial';
                }
            });
            expressCheckoutElement.on('click', (event) => {
                const options = {
                    emailRequired: !state.customer.email,
                    phoneNumberRequired: !state.customer.phoneNumber,
                    billingAddressRequired: false,
                    shippingAddressRequired: false,
                    allowedShippingCountries: [state.countryCode.toUpperCase()],
                };
                event.resolve(options);
            });
            expressCheckoutElement.on('confirm', (confirmEvent) => {
                return onExpressCheckoutConfirm(confirmEvent, state, stripe, elements);
            });
            expressCheckoutElement.mount(e);
            return () => {
                expressCheckoutElement.unmount();
            };
        }),
    });
});
const onExpressCheckoutConfirm = (confirmEvent, state, stripe, elements) => __awaiter(void 0, void 0, void 0, function* () {
    // We don't allow the user to pick an address in a different postal code than the one we have in the cart
    // because that can have an effect on prices.
    if (postalCodeHasChanged(state, confirmEvent)) {
        // TODO: Maybe use some other callback for this? It's not really that unexpected...
        state.onUnexpectedError('The postal code was changed during payment. Please enter your postal code again.');
        return;
    }
    yield savePaymentIntentCustomerData(state, confirmEvent);
    const { error: submitError } = yield elements.submit();
    if (submitError) {
        logger.error({ submitError }, 'An unexpected error occurred');
        state.onUnexpectedError(submitError.message || 'An unexpected error occurred');
        return;
    }
    const { error } = yield stripe.confirmPayment({
        elements,
        clientSecret: paymentIntentClientSecret(state),
        confirmParams: {
            // Note that we don't set this to the order confirmation page. This is because Stripe automatically
            // adds the payment intent id to the url of the return_url. We detect this and checks the status
            // of the payment intent to determine if the payment was successful or not.
            return_url: state.checkoutPageUrl,
        },
    });
    if (error) {
        logger.error({ error }, 'An unexpected error occurred');
        state.onUnexpectedError(error.message || 'An unexpected error occurred');
    }
    else {
        // No need to do anything here because in this case the payment was successful and the
        // user will be redirected
    }
});
const postalCodeHasChanged = (state, confirmEvent) => {
    var _a, _b;
    const { address } = getAddresses(confirmEvent);
    const postalCode = address === null || address === void 0 ? void 0 : address.postalCode;
    return postalCode && cleanPostalCode(postalCode) !== cleanPostalCode((_b = (_a = state.customer) === null || _a === void 0 ? void 0 : _a.address) === null || _b === void 0 ? void 0 : _b.postalCode);
};
const cleanPostalCode = (postalCode) => {
    return (postalCode !== null && postalCode !== void 0 ? postalCode : '').replace(/\s/g, '').replace(/-/g, '').toLowerCase();
};
const savePaymentIntentCustomerData = (state, confirmEvent) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const { address, billingAddress } = getAddresses(confirmEvent);
    const body = {
        address,
        billingAddress: billingAddress || null,
        email: ((_a = confirmEvent.billingDetails) === null || _a === void 0 ? void 0 : _a.email) || null,
        phoneNumber: ((_b = confirmEvent.billingDetails) === null || _b === void 0 ? void 0 : _b.phone) || null,
    };
    try {
        yield postWithRetries(`/carts/${state.id}/${state.version}/payment/customer`, state.checkoutApiBaseUrl, body, Type.Unknown(), state.tokenString);
    }
    catch (error) {
        logger.error({ error }, 'An unexpected error occurred');
        state.onUnexpectedError('An unexpected error occurred');
    }
});
//# sourceMappingURL=expressElements.js.map